import React, { useEffect, useState } from 'react';
import { Crown, Eye, Link2, RotateCw } from 'lucide-react';
import type { PublicCardView } from '#shared';

interface CardVisualProps {
  item: PublicCardView;
  sleeveUrl?: string | null;
  accentColor?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  playableGlow?: boolean;
  stackCount?: number;
  reducedMotion?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  onInspect?: () => void;
}

const SIZE_CLASSES: Record<NonNullable<CardVisualProps['size']>, string> = {
  xs: 'w-14 h-20 text-[9px]',
  sm: 'w-[70px] h-[98px] text-[10px]',
  md: 'w-[88px] h-[123px] text-[11px]',
  lg: 'w-[108px] h-[150px] text-xs',
};

const DEFAULT_MTG_CARD_BACK_URL =
  'https://cards.scryfall.io/normal/back/0/a/0aeebaf5-8c7d-4636-9e82-8c27447861f7.jpg';

/**
 * Default Playtable card sleeve: authentic regular Magic: The Gathering card back
 * (official Scryfall card back with offline Deckmaster SVG fallback), accented on the outer
 * sleeve rim by seat color so players remain distinguishable, or official Scryfall card art
 * from the player's deck/color-identity pool (never external image uploads).
 */
export function CardSleeveBack({
  sleeveUrl,
  accentColor,
  size = 'md',
  label,
  className = '',
}: {
  sleeveUrl?: string | null;
  accentColor?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  label?: string;
  className?: string;
}) {
  const [customFailed, setCustomFailed] = useState(false);
  const [defaultBackFailed, setDefaultBackFailed] = useState(false);

  useEffect(() => {
    setCustomFailed(false);
  }, [sleeveUrl]);

  const showCustomSleeve = Boolean(sleeveUrl) && !customFailed;

  return (
    <div
      style={accentColor ? { borderColor: accentColor } : undefined}
      className={`${SIZE_CLASSES[size]} rounded-lg relative select-none overflow-hidden shadow-lg bg-[#140e0a] border-[1.5px] border-[#3b2819] flex items-center justify-center ${className}`}
    >
      {showCustomSleeve ? (
        <img
          src={sleeveUrl!}
          alt="Card sleeve"
          onError={() => setCustomFailed(true)}
          className="w-full h-full object-cover pointer-events-none select-none"
        />
      ) : !defaultBackFailed ? (
        <img
          src={DEFAULT_MTG_CARD_BACK_URL}
          alt="Magic: The Gathering card back"
          onError={() => setDefaultBackFailed(true)}
          className="w-full h-full object-cover pointer-events-none select-none"
        />
      ) : (
        <svg
          viewBox="0 0 100 140"
          aria-hidden="true"
          className="w-full h-full pointer-events-none select-none"
        >
          <rect x="0" y="0" width="100" height="140" rx="6" fill="#120d0a" />
          <rect
            x="4.5"
            y="4.5"
            width="91"
            height="131"
            rx="3.5"
            fill="#6a3f21"
            stroke="#8f6339"
            strokeWidth="1.2"
          />
          <circle cx="10" cy="10" r="1.8" fill="#cbb28a" opacity="0.85" />
          <circle cx="90" cy="10" r="1.8" fill="#cbb28a" opacity="0.85" />
          <circle cx="10" cy="130" r="1.8" fill="#cbb28a" opacity="0.85" />
          <circle cx="90" cy="130" r="1.8" fill="#cbb28a" opacity="0.85" />
          <ellipse
            cx="50"
            cy="70"
            rx="35"
            ry="54"
            fill="#9b6a3e"
            stroke="#2e4f7a"
            strokeWidth="2.4"
          />
          <ellipse
            cx="50"
            cy="70"
            rx="31.5"
            ry="50.5"
            fill="#b68655"
            stroke="#3d2412"
            strokeWidth="1"
          />
          {/* Pentagon of the 5 Magic mana colors (W U B R G) */}
          <circle cx="50" cy="53" r="4.2" fill="#f8f4d8" stroke="#1b140e" strokeWidth="1.1" />
          <circle cx="65" cy="64" r="4.2" fill="#38bdf8" stroke="#1b140e" strokeWidth="1.1" />
          <circle cx="59" cy="82" r="4.2" fill="#3f3f46" stroke="#1b140e" strokeWidth="1.1" />
          <circle cx="41" cy="82" r="4.2" fill="#ef4444" stroke="#1b140e" strokeWidth="1.1" />
          <circle cx="35" cy="64" r="4.2" fill="#22c55e" stroke="#1b140e" strokeWidth="1.1" />
          {/* Classic Deckmaster lower plaque */}
          <rect
            x="28"
            y="104"
            width="44"
            height="9"
            rx="1.5"
            fill="#5e4b3b"
            stroke="#cbb28a"
            strokeWidth="0.9"
          />
        </svg>
      )}

      {label && (
        <span className="absolute bottom-1 left-1/2 -translate-x-1/2 px-1 py-[1px] rounded bg-[#0b101d] border border-slate-700 text-[6.5px] leading-tight uppercase tracking-wider text-slate-200 font-bold pointer-events-none whitespace-nowrap shadow">
          {label}
        </span>
      )}
    </div>
  );
}

export function CardVisual({
  item,
  sleeveUrl,
  accentColor,
  size = 'md',
  playableGlow = false,
  stackCount = 1,
  reducedMotion = false,
  onClick,
  onContextMenu,
  onInspect,
}: CardVisualProps) {
  const [imgFailed, setImgFailed] = useState(false);

  const cardDef = item.card;
  const activeName = item.flipped && cardDef?.backName ? cardDef.backName : cardDef?.name;
  const activeImg = item.flipped && cardDef?.backImageUrl ? cardDef.backImageUrl : cardDef?.imageUrl;
  const activeType = item.flipped && cardDef?.backTypeLine ? cardDef.backTypeLine : cardDef?.typeLine;

  const counterEntries = Object.entries(item.counters).filter(([, v]) => v !== 0);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={
        cardDef
          ? `${activeName}${item.tapped ? ' (Tapped)' : ''}${stackCount > 1 ? ` x${stackCount}` : ''}`
          : 'Face-down card'
      }
      onClick={onClick}
      onContextMenu={onContextMenu}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(e as unknown as React.MouseEvent);
        } else if (e.key.toLowerCase() === 'i') {
          e.preventDefault();
          onInspect?.();
        }
      }}
      className={`group relative select-none cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded-lg ${
        reducedMotion ? '' : 'transition-transform duration-200 ease-out'
      } ${item.tapped ? 'rotate-90 mx-3 brightness-75' : ''}`}
    >
      {stackCount > 1 && (
        <div
          aria-hidden="true"
          className="absolute inset-0 rounded-lg bg-slate-800 border border-slate-600/80 -z-10 translate-x-1.5 translate-y-1.5"
        />
      )}

      {!cardDef ? (
        <CardSleeveBack sleeveUrl={sleeveUrl} accentColor={accentColor} size={size} label="Hidden" />
      ) : (
        <div
          className={`${SIZE_CLASSES[size]} rounded-lg overflow-hidden relative bg-slate-900 border ${
            playableGlow
              ? 'border-cyan-300 ring-2 ring-cyan-400/75 shadow-[0_0_18px_rgba(34,211,238,0.65)]'
              : item.isCommander
              ? 'border-amber-400/85 shadow-md'
              : 'border-slate-700/90 shadow-md'
          } flex flex-col justify-between`}
        >
          {activeImg && !imgFailed ? (
            <img
              src={activeImg}
              alt={activeName ?? 'Card'}
              loading="lazy"
              onError={() => setImgFailed(true)}
              className="w-full h-full object-cover pointer-events-none"
            />
          ) : (
            <div className="p-1.5 flex flex-col justify-between h-full bg-gradient-to-b from-slate-800 via-slate-900 to-slate-950">
              <div>
                <div className="font-semibold text-slate-100 leading-tight line-clamp-2">{activeName}</div>
                {cardDef.manaCost && (
                  <div className="text-[9px] text-amber-300/90 font-mono mt-0.5">{cardDef.manaCost}</div>
                )}
              </div>
              <div className="text-[8px] text-slate-400 truncate">{activeType}</div>
              {(cardDef.power || cardDef.loyalty) && (
                <div className="self-end px-1 py-0.5 rounded bg-slate-950/90 border border-slate-700 text-[9px] font-bold text-slate-200">
                  {cardDef.loyalty ? `✦ ${cardDef.loyalty}` : `${cardDef.power}/${cardDef.toughness}`}
                </div>
              )}
            </div>
          )}

          {item.faceDown && (
            <div className="absolute top-1 left-1 px-1 py-0.5 rounded bg-purple-950/90 border border-purple-400/60 text-[8px] text-purple-200 flex items-center gap-0.5">
              <Eye className="w-2.5 h-2.5" />
              <span>FD</span>
            </div>
          )}

          {item.isCommander && (
            <div className="absolute top-1 right-1 p-0.5 rounded bg-amber-950/90 border border-amber-400/70 text-amber-300">
              <Crown className="w-2.5 h-2.5" />
            </div>
          )}

          {item.isToken && (
            <div className="absolute top-1 left-1 px-1 rounded bg-emerald-950/90 border border-emerald-400/60 text-[8px] font-bold text-emerald-300">
              TOKEN
            </div>
          )}

          {item.attachedToId && (
            <div className="absolute bottom-1 left-1 p-0.5 rounded bg-sky-950/90 border border-sky-400/60 text-sky-300">
              <Link2 className="w-2.5 h-2.5" />
            </div>
          )}

          {cardDef.layout !== 'normal' && cardDef.backName && (
            <div className="absolute bottom-1 left-1 p-0.5 rounded bg-slate-950/85 border border-slate-500/60 text-slate-200">
              <RotateCw className="w-2.5 h-2.5" />
            </div>
          )}
        </div>
      )}

      {stackCount > 1 && (
        <div className="absolute -top-2 -right-2 px-1.5 py-0.5 rounded-full bg-amber-500 text-slate-950 font-extrabold text-[10px] shadow-md border border-amber-200">
          ×{stackCount}
        </div>
      )}

      {counterEntries.length > 0 && (
        <div className="absolute -bottom-1.5 right-0.5 flex flex-wrap gap-0.5 justify-end max-w-full pointer-events-none">
          {counterEntries.map(([k, v]) => (
            <span
              key={k}
              className="px-1 py-0.5 rounded bg-indigo-600 text-white font-bold text-[9px] leading-none shadow border border-indigo-300/60"
            >
              {k === '+1/+1' ? `+${v}/+${v}` : k === 'loyalty' ? `✦${v}` : `${k}:${v}`}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
